// Screenplay fragments (SuiteEditor's ScreenplayEditing): Fountain, classified in context, edited as Final Draft
// edits — Return moves to the element that usually comes next, Tab (and ⌘1–⌘6) choose the line's element, and the
// text of scene headings, cues and transitions is typed in capitals. A chosen element is written into the Fountain
// only as far as the text needs it (`.` heading, `>` transition, `!` action, `@` cue) when the caret leaves the line.
import { EditorSelection, Transaction, type EditorState, type StateCommand, type TransactionSpec } from '@codemirror/state';
import {
  classifyFountain, fountainContentRange, isCharacterCue, isSceneHeading, isSongLine, songTitle, type ClassifiedElement, type ClassifiedLine,
} from '@writing-suite/core';
import { EditBuilder } from './session.ts';
import {
  Exact, ExactCaret, isMusical, isPlayLikeFormat, isStage, layoutOf, pendingElementField, scriptFormatOf, setPendingElement, type PendingElement,
} from './state.ts';

/** The elements the element menu and ⌘1–⌘6 offer, in order (`Fountain.Element.writable`). */
export const WRITABLE_ELEMENTS: readonly ClassifiedElement[] = ['sceneHeading', 'action', 'character', 'parenthetical', 'dialogue', 'transition'];
/** A play's and a radio play's (`ScriptFormat.writableElements`): no scene headings, and lyrics chosen like any other
 *  line. */
export const STAGE_ELEMENTS: readonly ClassifiedElement[] = ['action', 'character', 'parenthetical', 'dialogue', 'transition', 'lyric'];
export const writableElements = (format: string): readonly ClassifiedElement[] => (isPlayLikeFormat(format) ? STAGE_ELEMENTS : WRITABLE_ELEMENTS);

const ELEMENT_NAMES: Partial<Record<ClassifiedElement, string>> = {
  sceneHeading: 'Scene Heading', action: 'Action', character: 'Character', parenthetical: 'Parenthetical', dialogue: 'Dialogue',
  transition: 'Transition', lyric: 'Lyric', centered: 'Centred', section: 'Section', synopsis: 'Synopsis', note: 'Note',
  pageBreak: 'Page Break', titlePage: 'Title Page', boneyard: 'Boneyard', blank: 'Blank',
};
/** `ScriptFormat.name(of:)`: a play's action is its stage directions, its transitions the blackouts and curtains; a
 *  radio play's action its technical directions (sound and music cues). */
export function elementName(format: string, element: ClassifiedElement): string {
  if (format === 'radio' && element === 'action') return 'Technical Direction';
  if (isStage(format) && element === 'action') return 'Stage Direction';
  if (isStage(format) && element === 'transition') return 'Blackout or Curtain';
  return ELEMENT_NAMES[element] ?? element;
}

/** The elements Tab steps through outside a speech (`ScreenplayKeys.cycle`): a play's from a name to a direction to its
 *  end, with no scene headings. */
const cycleOf = (format: string): readonly ClassifiedElement[] =>
  (isPlayLikeFormat(format) ? ['character', 'action', 'transition'] : ['action', 'character', 'transition', 'sceneHeading']);

const lower = /\p{Ll}/u;
const hasLowercase = (s: string) => lower.test(s);

/** The caret's line in a screenplay fragment, classified in context (`ScreenplayLine`). Offsets are document offsets. */
export interface ScreenplayLine {
  /** The line, without its newline. */
  from: number;
  to: number;
  leadingMarker: number;
  contentLength: number;
  /** Its element, with any element the writer chose for it but hasn't typed yet. */
  element: ClassifiedElement;
  /** The element its text makes it on its own. */
  written: ClassifiedElement;
  previous: ClassifiedElement | null;
  index: number;
  text: string;
}

export const inSpeech = (l: ScreenplayLine) => l.element === 'dialogue' || l.element === 'parenthetical';
export const previousIsBlank = (l: ScreenplayLine) => l.previous === null || l.previous === 'blank';
/** The text without forcing markers, surrounding markers or spaces. */
export const content = (l: ScreenplayLine) => l.text.slice(l.leadingMarker, l.leadingMarker + l.contentLength).trim();
/** A direction naming a song (`Song: …`), which a musical shows as one (`ScreenplayLine.isSong`). */
export const isSong = (l: ScreenplayLine) => l.element === 'action' && isSongLine(content(l));

/** What to restyle or classify around `[a, b)` of a body (`screenplayRange`): the block of lines holding it and the
 *  blocks either side (an element depends on the lines around it, never across more than a blank line); everything
 *  when the body has a boneyard (`/* … *\/` can span blocks). Offsets within `body`. */
export function screenplayRange(body: string, a: number, b: number): [number, number] {
  const end = body.length;
  if (body.includes('/*')) return [0, end];
  const lineStart = (loc: number) => { let i = Math.min(loc, end); while (i > 0 && body.charCodeAt(i - 1) !== 10) i--; return i; };
  const lineEnd = (loc: number) => { let i = Math.max(loc, 0); while (i < end && body.charCodeAt(i) !== 10) i++; return i; };
  const isBlank = (s: number, e: number) => { for (let i = s; i < e; i++) { const c = body.charCodeAt(i); if (c !== 32 && c !== 9) return false; } return true; };
  const blockStart = (loc: number) => {
    let s = lineStart(loc);
    while (s > 0) { const p = lineStart(s - 1); if (isBlank(p, s - 1)) break; s = p; }
    return s;
  };
  const blockEnd = (loc: number) => {
    let e = lineEnd(loc);
    while (e < end) { const n = lineEnd(e + 1); if (isBlank(e + 1, n)) break; e = n; }
    return e;
  };
  const previousBlockStart = (start: number) => {
    let s = start;
    while (s > 0) { s = lineStart(s - 1); if (!isBlank(s, lineEnd(s))) return blockStart(s); }
    return 0;
  };
  const nextBlockEnd = (e0: number) => {
    let e = e0;
    while (e < end) { e = lineEnd(e + 1); if (!isBlank(lineStart(e), e)) return blockEnd(e); }
    return end;
  };
  return [previousBlockStart(blockStart(Math.max(0, a))), nextBlockEnd(blockEnd(Math.min(end, b)))];
}

/** The lines of `[from, to)` of a screenplay body, classified while editing (a cue waiting for its dialogue counts). */
export function classifyRange(body: string, from: number, to: number): ClassifiedLine[] {
  return classifyFountain(body, { editing: true, range: [from, to - from], limit: body.length, atDocumentStart: from === 0 });
}

/** The screenplay line holding `pos` (`ScriveningsSession.screenplayLine`), or null outside a screenplay. */
export function screenplayLine(state: EditorState, pos: number): ScreenplayLine | null {
  const layout = layoutOf(state);
  const i = layout.bodyAt(pos);
  if (i < 0 || !layout.isScreenplay(i)) return null;
  const base = layout.from[i];
  const body = state.doc.sliceString(base, layout.to[i]);
  const at = Math.min(pos, layout.to[i]) - base;
  const [rs, re] = screenplayRange(body, at, at);
  const lines = classifyRange(body, rs, re);
  let k = -1;
  for (let j = 0; j < lines.length; j++) if (lines[j].location <= at) k = j;
  if (k < 0) return null;
  const line = lines[k];
  const pending = state.field(pendingElementField);
  const element = pending && pending.lineStart === base + line.location ? pending.element : line.element;
  const [, contentLength] = fountainContentRange(line);
  return {
    from: base + line.location, to: base + line.location + line.length, leadingMarker: line.leadingMarker, contentLength,
    element, written: line.element, previous: k > 0 ? lines[k - 1].element : null, index: i,
    text: body.slice(line.location, line.location + line.length),
  };
}

const spec = (b: EditBuilder, selection: number, effects: TransactionSpec['effects'] = [], userEvent = 'input'): TransactionSpec => ({
  changes: b.changes, selection: EditorSelection.cursor(selection), effects,
  annotations: [Exact.of(true), ExactCaret.of(true), Transaction.userEvent.of(userEvent)], scrollIntoView: true,
});

/** Writes the chosen element into the Fountain, as far as the text needs it, and forgets it (`resolvePending`).
 *  Returns how many characters went in before the caret. */
function resolveInto(b: EditBuilder, state: EditorState, pending: PendingElement, caret: number): number {
  const line = screenplayLine(state, pending.lineStart);
  if (!line || line.from !== pending.lineStart || content(line) === '' || line.written === pending.element) return 0;
  let prefix: string;
  switch (pending.element) {
    case 'sceneHeading': prefix = previousIsBlank(line) ? '.' : '\n.'; break; // a heading follows a blank line
    case 'transition': prefix = '>'; break;
    case 'action': prefix = '!'; break;
    case 'character':
      if (!previousIsBlank(line)) prefix = '\n'; // a cue follows a blank line
      else if (hasLowercase(content(line))) prefix = '@';
      else return 0;
      break;
    default: return 0;
  }
  b.replace(pending.lineStart, pending.lineStart, prefix);
  return caret >= pending.lineStart ? prefix.length : 0;
}

/** The transaction writing a pending element in (for when the caret has left its line), or null. */
export function resolvePendingSpec(state: EditorState): TransactionSpec | null {
  const pending = state.field(pendingElementField);
  if (!pending) return null;
  const b = new EditBuilder(state);
  const sel = state.selection.main;
  const shift = resolveInto(b, state, pending, sel.head);
  if (b.empty) return { effects: setPendingElement.of(null) };
  return {
    changes: b.changes, effects: setPendingElement.of(null),
    selection: EditorSelection.range(sel.anchor + (sel.anchor >= pending.lineStart ? shift : 0), sel.head + shift),
    annotations: [Exact.of(true), ExactCaret.of(true), Transaction.userEvent.of('input')],
  };
}

/** Whether the caret has left the line of a pending element (`ScreenplayKeys.selectionChanged`). */
export function caretLeftPending(state: EditorState): boolean {
  const pending = state.field(pendingElementField);
  if (!pending) return false;
  const caret = state.selection.main.head;
  const doc = state.doc;
  if (pending.lineStart > doc.length) return true;
  const end = doc.lineAt(pending.lineStart).to;
  return caret < pending.lineStart || caret > end;
}

/** Return (or Shift-Return, `lineBreak`) in a screenplay (`ScreenplayKeys.newline`). False outside one. */
export function screenplayNewline(lineBreak: boolean): StateCommand {
  return ({ state, dispatch }) => {
    const sel = state.selection.main;
    const before = screenplayLine(state, sel.from);
    if (!before) return false;
    let element = before.element;
    // A name in capitals after a blank line is a cue as soon as Return makes room for its dialogue.
    if (element === 'action' && before.leadingMarker === 0 && previousIsBlank(before) && isCharacterCue(content(before))
      && !isSceneHeading(content(before))) element = 'character';
    const b = new EditBuilder(state);
    let from = sel.from, to = sel.to;
    const pending = state.field(pendingElementField);
    const effects = [];
    if (pending && pending.lineStart === before.from) {
      const shift = resolveInto(b, state, pending, from);
      from += shift; to += shift;
      effects.push(setPendingElement.of(null));
    }
    // Return inside "(beat|)" steps past the closing parenthesis first.
    if (from === to && element === 'parenthetical') {
      const lineEnd = b.doc.lineAt(from).to;
      if (b.slice(from, lineEnd).trim() === ')') { from = lineEnd; to = lineEnd; }
    }
    // A cue or parenthetical goes on to dialogue, and an empty line (an empty speech ends there) just moves down;
    // anything else starts a new paragraph after a blank line.
    const sameBlock = lineBreak || content(before) === '' || element === 'character' || element === 'parenthetical';
    const text = sameBlock ? '\n' : '\n\n';
    b.replace(from, to, text);
    const caret = from + text.length;
    // In a play, after a speech or a direction someone usually speaks: the new line waits for a name.
    if (isPlayLikeFormat(scriptFormatOf(state)) && !sameBlock && (element === 'dialogue' || element === 'lyric' || element === 'action')) {
      effects.push(setPendingElement.of({ lineStart: caret, element: 'character' }));
    }
    dispatch(state.update(spec(b, caret, effects)));
    return true;
  };
}

/** Makes the caret's line an element (`ScreenplayKeys.setElement`: Tab, ⌘1–⌘6, the element menu). */
export function setScreenplayElement(element: ClassifiedElement): StateCommand {
  return ({ state, dispatch }) => {
    const line = screenplayLine(state, state.selection.main.head);
    if (!line || state.readOnly) return false;
    const caret = state.selection.main.head;
    const start = line.from;
    const b = new EditBuilder(state);
    // Lyrics are marked in the text (`~`), in a speech or out of one.
    if (element === 'lyric') {
      const lyric = '~' + content(line);
      if (lyric === line.text) return true;
      const offset = caret - start - line.leadingMarker;
      b.replace(line.from, line.to, lyric);
      dispatch(state.update(spec(b, start + 1 + Math.min(Math.max(0, offset), content(line).length))));
      return true;
    }
    if (element === 'dialogue' || element === 'parenthetical') {
      if (!inSpeech(line)) return true; // dialogue only exists under a character
      const c = content(line);
      const bare = c.startsWith('(') && c.endsWith(')') && [...c].length >= 2 ? c.slice(1, -1) : c;
      const rewritten = element === 'parenthetical' ? `(${bare})` : bare;
      if (rewritten === line.text) return true;
      const offset = caret - start;
      b.replace(line.from, line.to, rewritten);
      // Keep the caret with the words: inside the parentheses, or where it was.
      const inside = element === 'parenthetical' ? Math.min(Math.max(1, offset + 1), rewritten.length - 1) : Math.max(0, offset - 1);
      dispatch(state.update(spec(b, start + Math.min(inside, rewritten.length))));
      return true;
    }
    // Everything else: forcing marks stripped (and a song's label, leaving the song), capitals for what's printed in
    // capitals.
    const atEnd = caret >= line.to;
    const c = isSong(line) ? (songTitle(content(line)) ?? '') : content(line);
    const rewritten = element === 'character' || element === 'transition' || element === 'sceneHeading' ? c.toUpperCase() : c;
    let selection = caret;
    if (rewritten !== line.text) {
      b.replace(line.from, line.to, rewritten);
      selection = start + (atEnd ? rewritten.length : Math.min(rewritten.length, Math.max(0, caret - start - line.leadingMarker)));
    }
    dispatch(state.update(spec(b, selection, [setPendingElement.of({ lineStart: start, element })])));
    return true;
  };
}

/** Makes the caret's line a musical's song (`ScreenplayKeys.setSong`: Tab, ⌘7): a direction starting `Song: `, after a
 *  blank line, the caret where the title goes on. */
export const setScreenplaySong: StateCommand = ({ state, dispatch }) => {
  const line = screenplayLine(state, state.selection.main.head);
  if (!line || state.readOnly) return false;
  if (isSong(line)) return true;
  const rewritten = (previousIsBlank(line) ? '' : '\n') + 'Song: ' + content(line);
  const b = new EditBuilder(state);
  b.replace(line.from, line.to, rewritten);
  dispatch(state.update(spec(b, line.from + rewritten.length, [setPendingElement.of(null)])));
  return true;
};

/** Tab (or Shift-Tab): in a speech, dialogue ⇄ parenthetical; elsewhere the next of action, character, transition and
 *  scene heading (`ScreenplayKeys.tab`). False outside a screenplay. */
export function screenplayTab(backwards: boolean): StateCommand {
  return (target) => {
    const line = screenplayLine(target.state, target.state.selection.main.head);
    if (!line || target.state.readOnly) return false;
    if (inSpeech(line)) return setScreenplayElement(line.element === 'parenthetical' ? 'dialogue' : 'parenthetical')(target);
    const format = scriptFormatOf(target.state);
    const sel = target.state.selection.main;
    // In a play, Tab at the end of a direction that broke into a speech goes back to the speech: its speaker's name
    // again, and a line for the rest of what they say.
    if (isPlayLikeFormat(format) && !backwards && line.element === 'action' && content(line) !== '' && sel.empty && sel.head === line.to) {
      const speaker = interruptedSpeaker(target.state, line);
      if (speaker !== null) {
        const b = new EditBuilder(target.state);
        const text = `\n\n${speaker}\n`;
        b.replace(sel.head, sel.head, text);
        target.dispatch(target.state.update(spec(b, sel.head + text.length)));
        return true;
      }
    }
    // A musical steps from a direction to a song before its blackout: name → direction → song → blackout.
    if (isMusical(target.state)) {
      const order: readonly (ClassifiedElement | 'song')[] = ['character', 'action', 'song', 'transition'];
      const i = order.indexOf(isSong(line) ? 'song' : line.element);
      const choice = i >= 0 ? order[(i + (backwards ? order.length - 1 : 1)) % order.length] : order[backwards ? order.length - 1 : 0];
      return choice === 'song' ? setScreenplaySong(target) : setScreenplayElement(choice)(target);
    }
    const cycle = cycleOf(format);
    const current = cycle.indexOf(line.element);
    let next: ClassifiedElement;
    if (current >= 0) next = cycle[(current + (backwards ? cycle.length - 1 : 1)) % cycle.length];
    // A line that's none of them yet: a screenplay's goes on from action, a play's starts with a name.
    else if (isPlayLikeFormat(format)) next = backwards ? cycle[cycle.length - 1] : cycle[0];
    else next = cycle[backwards ? cycle.length - 1 : 1];
    return setScreenplayElement(next)(target);
  };
}

/** `ScreenplayKeys.interruptedSpeaker`: who was speaking when the direction holding `line` broke in — the cue of the
 *  speech just above its block. */
function interruptedSpeaker(state: EditorState, line: ScreenplayLine): string | null {
  const layout = layoutOf(state);
  const base = layout.from[line.index];
  if (line.from <= base) return null;
  const before = state.doc.sliceString(base, line.from - 1);
  const lines = classifyFountain(before, { editing: true });
  let blank = -1;
  for (let k = lines.length - 1; k >= 0; k--) if (lines[k].element === 'blank') { blank = k; break; }
  if (blank <= 0) return null;
  if (!['dialogue', 'parenthetical', 'lyric'].includes(lines[blank - 1].element)) return null;
  let cue: ClassifiedLine | undefined;
  for (let k = blank - 1; k >= 0; k--) if (lines[k].element === 'character') { cue = lines[k]; break; }
  if (!cue) return null;
  const [loc, len] = fountainContentRange(cue);
  const name = before.slice(loc, loc + len);
  const speaker = (name.split('(')[0] ?? name).replaceAll('^', '').replace(/^[ \t]+|[ \t]+$/g, '');
  return speaker === '' ? null : speaker;
}

/** What typing should insert in a screenplay instead (`ScreenplayKeys.transform`): capitals in headings, cues and
 *  transitions, and the line so far when "int." makes it a heading. Null to type it as it is. */
export function transformTyped(state: EditorState, a: number, b: number, text: string): { a: number; b: number; text: string } | null {
  if (text.includes('\n')) return null;
  const line = screenplayLine(state, a);
  if (!line || b > line.to) return null;
  if (line.element === 'sceneHeading' || line.element === 'character' || line.element === 'transition') {
    return hasLowercase(text) ? { a, b, text: text.toUpperCase() } : null;
  }
  // Typing "int." makes a scene heading: capitalise the line so far, as it's now one.
  if (line.element === 'action' && previousIsBlank(line) && line.leadingMarker === 0) {
    const before = state.doc.sliceString(line.from, a);
    const after = state.doc.sliceString(b, line.to);
    if (hasLowercase(before + text) && isSceneHeading((before + text + after).trim())) {
      return { a: line.from, b, text: (before + text).toUpperCase() };
    }
  }
  return null;
}

/** Scripts keep `--`: an automatic em dash over it is declined (`declinesSubstitution`). */
export function declinesSubstitution(state: EditorState, a: number, b: number, text: string): boolean {
  if ((text !== '—' && text !== '–') || b <= a) return false;
  return /^-+$/.test(state.doc.sliceString(a, b));
}

/** Pasted text in a screenplay is Fountain already: only line endings change (`ScreenplayKeys.pasted`). */
export const pastedScript = (text: string) => text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

/** The caret line's element by the name this kind of script gives it (`screenplayElementName`): "Stage Direction" in a
 *  play, "Technical Direction" in a radio play, "Song" for a musical's song. Null outside a script. */
export function screenplayElementNameAt(state: EditorState): string | null {
  const line = screenplayLine(state, state.selection.main.head);
  if (!line) return null;
  if (isMusical(state) && isSong(line)) return 'Song';
  return elementName(scriptFormatOf(state), line.element);
}

/** The element of the caret's line, when it's in a screenplay. */
export function screenplayElementAt(state: EditorState): ClassifiedElement | null {
  return screenplayLine(state, state.selection.main.head)?.element ?? null;
}
